// What the Products, Add a product, product settings and Page layout screens read and write. Every
// call goes as the staff member, through src/admin/api.js: direct reads and writes under row level
// security (docs/data-model.md 10.4: "catalogue and content edits, layouts, masterclass_offers
// (owner)"), and the functions stripe-prices (owner and admin: amounts, unmapped), sign-file (covers)
// and sign-upload (covers). The database decides who may do what and audits it: catalogue inserts and
// state changes, layout publishes and the price map are audited by its triggers.
import { ukToIso, labelFromStripeName } from '../ui/format.js'
import { AdminError } from '../api.js'
import { duplicateRow, newProductRow, scheduleSessions, scheduleLabel, sectionsToSave, productPrices, weeksFor, modulesFor, startingSections, runsFor, paidMonthly } from './model.js'
import { nextCohort } from '../content/model.js'

// created_by says a product was made in admin (model.js madeInAdmin)
export const CATALOGUE_COLUMNS = 'id, slug, path, name, tagline, type, access_kind, state, look, own_look, own_look_contrast_ok, cover_path, moved_in, questions_email, live_sessions_audience, release_basis, join_opens_minutes, default_session_minutes, watched_share, min_access_months, min_access_from, term_months, terms_line, offer_product_id, certificate_on, certificate_rule, certificate_min_sessions, certificate_prefix, settings, sort_order, duplicated_from_id, created_by'
const MODULE_COLUMNS = 'id, product_id, key, number, label, title, sort_order'
const BILLING_COLUMNS = 'product_id, mode, access_email_on'
const CHECKLIST_COLUMNS = 'id, product_id, sort_order, body, action_kind, action_label, audience'

const isoStartOfDay = nowMs => {
  const d = new Date(nowMs)
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - 24 * 3600e3).toISOString()
}

// Everything the Products page shows, in 1 go. Owner and admin read all of it at 2-step login.
export async function loadProductsPage(api, nowMs) {
  const q = (table, cols, f) => {
    let query = api.from(table).select(cols)
    if (f) query = f(query)
    return api.run(query)
  }
  const since = isoStartOfDay(nowMs)
  const [products, billing, checklist, cohorts, sessions, offers, spaces, clientSessions, priceMap, mapProducts, balances, webinars, modules, recordings, steps] = await Promise.all([
    q('catalogue', CATALOGUE_COLUMNS, x => x.order('access_kind', { ascending: false }).order('sort_order')),
    q('catalogue_billing', 'product_id, mode, access_email_on'),
    q('checklist_items', CHECKLIST_COLUMNS, x => x.order('product_id').order('sort_order')),
    q('cohorts', 'id, product_id, name, kind, starts_on, ends_on, weeks_total, sort_order'),
    q('sessions', 'id, product_id, cohort_id, title, number, starts_at, duration_minutes, status', x => x.gte('starts_at', since).order('starts_at')),
    q('masterclass_offers', 'product_id, offer, owner_card'),
    q('client_spaces', 'id, product_id, client_person_id, opened_at', x => x.is('ended_at', null)),
    q('client_sessions', 'id, space_id, number, starts_at', x => x.gte('starts_at', since).order('starts_at')),
    q('price_map', 'stripe_price_id, status, secures_place'),
    q('price_map_products', 'stripe_price_id, product_id'),
    q('price_map_balances', 'place_price_id, balance_price_id, pay_button'),
    q('webinars', 'id, product_id, starts_at, duration_minutes, shown, replay_up, title'),
    q('modules', 'id, product_id, is_published'),
    q('recordings', 'id, product_id, title, is_published', x => x.order('sort_order')),
    q('steps', 'id, product_id, section, is_published'),
  ])
  const masterclassIds = products.filter(p => p.type === 'masterclass').map(p => p.id)
  const mcRecordings = recordings.filter(r => masterclassIds.includes(r.product_id)).map(r => r.id)
  const clientIds = [...new Set(spaces.map(s => s.client_person_id))]
  const [lengths, people] = await Promise.all([
    mcRecordings.length ? q('recording_content', 'recording_id, duration_seconds', x => x.in('recording_id', mcRecordings)) : [],
    clientIds.length ? q('people', 'id, full_name, first_name', x => x.in('id', clientIds)) : [],
  ])
  return {
    products, billing, checklist, cohorts, sessions, offers, spaces, clientSessions, people, webinars, modules, recordings, steps,
    map: { priceMap, mapProducts, balances },
    recordingLengths: Object.fromEntries(lengths.map(l => [l.recording_id, l.duration_seconds])),
  }
}

// Stripe's amounts for every mapped price the page shows (stripe-prices, owner and admin), at most 50
// ids a call. Nothing is stored: the amounts are Stripe's.
export async function loadAmounts(api, products, map) {
  const ids = [...new Set(products.flatMap(p => { const x = productPrices(p.id, map); return [...x.full, ...x.places, ...x.balances] }))]
  if (!ids.length) return { prices: {} }
  const prices = {}
  for (let i = 0; i < ids.length; i += 50) {
    const answer = await api.stripe_prices({ action: 'amounts', price_ids: ids.slice(i, i + 50) })
    Object.assign(prices, answer?.prices || {})
  }
  return { prices }
}

// The products' cover photos, through 2-minute signed links (sign-file's covers branch). A cover not
// in storage yet is left out, and the card draws its look instead.
export async function loadCovers(api, products) {
  const ids = products.filter(p => p.cover_path).map(p => p.id).slice(0, 20)
  if (!ids.length) return {}
  const answer = await api.sign_file({ covers: ids })
  return Object.fromEntries(Object.entries(answer?.covers || {}).map(([id, c]) => [id, c.url]))
}

// ---- Writes on Products -------------------------------------------------------------------------

const one = rows => (Array.isArray(rows) ? rows[0] : rows)

async function changed(api, query) {
  const rows = await api.run(query)
  if (!rows || (Array.isArray(rows) && !rows.length)) throw new AdminError('not_there', 0, "That isn't there any more, or it isn't yours to change.")
  return one(rows)
}

// Live or Draft (audited as product_state_changed)
export function setState(api, productId, state) {
  return changed(api, api.from('catalogue').update({ state }).eq('id', productId).select('id, state'))
}

// The certificate panel: on or off, and the rule ("Came to at least 5 live sessions" is 5)
export function setCertificate(api, productId, { on, rule }) {
  const patch = { certificate_on: on }
  if (rule !== undefined) {
    patch.certificate_rule = rule
    patch.certificate_min_sessions = rule === 'attended_min' ? 5 : null
  }
  return changed(api, api.from('catalogue').update(patch).eq('id', productId).select('id, certificate_on, certificate_rule, certificate_min_sessions'))
}

// The welcome checklist: a plain step for everyone (new steps have no button), a new order, removed
export async function addStep(api, productId, body, sortOrder) {
  return one(await api.run(api.from('checklist_items').insert({ product_id: productId, body, sort_order: sortOrder }).select(CHECKLIST_COLUMNS)))
}
export async function reorderSteps(api, changes) {
  const out = []
  for (const c of changes) out.push(await changed(api, api.from('checklist_items').update({ sort_order: c.sort_order }).eq('id', c.id).select('id, sort_order')))
  return out
}
export function removeStep(api, id) {
  return changed(api, api.from('checklist_items').delete().eq('id', id).select('id'))
}

// Duplicate: the row as a draft, then its checklist, then each page layout as published, as version 1,
// then its weeks or modules (new rows, the same keys and words) and its Resources tabs (their names and
// notes, never the files). Never its recordings, cohorts, groups, sessions, client spaces or prices.
// Not 1 transaction: a step that fails leaves a draft nobody sees, and the flash says so.
export async function duplicateProduct(api, src, all, actorId) {
  const row = duplicateRow(src, all, actorId)
  const made = one(await api.run(api.from('catalogue').insert(row).select('id, slug, name, state')))
  let partial = false
  try {
    const steps = (await api.run(api.from('checklist_items').select('body, sort_order, action_kind, action_label, action_target, audience').eq('product_id', src.id)))
    if (steps.length) await api.run(api.from('checklist_items').insert(steps.map(s => ({ ...s, product_id: made.id }))).select('id'))
  } catch { partial = true }
  try {
    const layouts = await api.run(api.from('layouts').select('page, draft_sections, published_version_id').eq('product_id', src.id))
    const versionIds = layouts.map(l => l.published_version_id).filter(Boolean)
    const versions = versionIds.length ? await api.run(api.from('layout_versions').select('id, sections').in('id', versionIds)) : []
    for (const l of layouts) {
      const sections = versions.find(v => v.id === l.published_version_id)?.sections || l.draft_sections
      const layout = one(await api.run(api.from('layouts').insert({ product_id: made.id, page: l.page, draft_sections: sections, has_draft: false }).select('id')))
      if (versions.some(v => v.id === l.published_version_id)) {
        const v = one(await api.run(api.from('layout_versions').insert({ layout_id: layout.id, version: 1, sections, note: `Copied from ${src.name}`.slice(0, 200), published_by: actorId }).select('id')))
        await changed(api, api.from('layouts').update({ published_version_id: v.id }).eq('id', layout.id).select('id'))
      }
    }
  } catch { partial = true }
  let copied = 0
  try {
    const mods = await api.run(api.from('modules').select('key, number, label, title, teaser, pack_line, sort_order, is_published').eq('product_id', src.id).order('sort_order'))
    if (mods.length) copied = (await api.run(api.from('modules').insert(mods.map(m => ({ ...m, product_id: made.id }))).select('id'))).length
  } catch { partial = true }
  try {
    const tabs = await api.run(api.from('resource_sections').select('name, sort_order, tab_note').eq('product_id', src.id).order('sort_order'))
    if (tabs.length) await api.run(api.from('resource_sections').insert(tabs.map(t => ({ ...t, product_id: made.id }))).select('id'))
  } catch { partial = true }
  const counts = src.type === 'live_cohort' ? { weeks: copied } : src.type === 'on_demand' ? { modules: copied } : {}
  return { made, partial, ...counts }
}

// Make a product's Stripe purchases live (owner only: catalogue_billing's update policy; audited by its
// trigger). The access email and no self-cancel keep their defaults, on and true.
export function setPurchasesLive(api, productId) {
  return changed(api, api.from('catalogue_billing').update({ mode: 'live' }).eq('product_id', productId).select(BILLING_COLUMNS))
}

// ---- Add a product ------------------------------------------------------------------------------

// The Stripe prices with no row in the price map (stripe-prices, owner and admin), active ones only
export async function loadUnmapped(api) {
  const answer = await api.stripe_prices({ action: 'unmapped' })
  return (answer?.prices || []).filter(p => p.active !== false)
}

// The row, then each piece its type comes with, each its own write, so 1 failing leaves the draft and
// says what's left to do:
// - a live cohort: its weeks, Cohort 1 (dated from the schedule when a first date is picked, else with
//   none) and 1 session a week carrying its week, then its starting Resources tabs;
// - an on-demand course: its modules, then its Downloads tab where downloads are ticked;
// - a mastermind: Group 1;
// - a paid resource pack: its Downloads tab;
// - the ticked prices (owner only, never a private client space);
// - an owner's paid product, other than a client space: its Stripe purchases made live (decision 2 of
//   the product-types plan), so buying 1 of its prices opens it once it's switched on. A product paid
//   monthly (a mastermind's membership, a subscription, or a ticked price that repeats) stays in shadow
//   (held): the plan's step 8.4, the read of the Accelerator's old payment flows (stripe-webhook and n8n's
//   Payment Status Sync, which see every Stripe subscription), comes first, and until then it's given by
//   hand. The owner makes it live from its settings after that;
// - the cover photo.
export async function createProduct(api, form, all, actorId, { owner }) {
  const row = newProductRow(form, all, actorId)
  const made = one(await api.run(api.from('catalogue').insert(row)
    .select('id, slug, path, name, type, access_kind, state, look, release_basis, default_session_minutes, settings')))
  const result = { made, owner: Boolean(owner), cohort: null, sessions: 0, weeks: 0, modules: 0, group: null, tabs: 0, live: false, held: false, problems: [] }
  const paid = made.access_kind === 'paid'
  const pieces = form.pieces || []
  const insert = async (table, rows, cols = 'id') => api.run(api.from(table).insert(rows).select(cols))

  if (made.type === 'live_cohort') {
    let weeks = []
    try {
      weeks = await insert('modules', weeksFor(form.count).map(m => ({ ...m, product_id: made.id })), MODULE_COLUMNS)
      result.weeks = weeks.length
    } catch { result.problems.push('weeks') }
    try {
      const plan = form.startOn ? scheduleSessions({ startOn: form.startOn, day: form.day, at: form.at, count: form.count }, ukToIso) : []
      const cohort = one(await insert('cohorts', {
        product_id: made.id, name: 'Cohort 1', kind: 'intake', schedule_label: plan.length ? scheduleLabel(form.day, form.at) : null,
        starts_on: plan[0]?.starts_on || null, weeks_total: form.count, number: 1, sort_order: 1, created_by: actorId,
      }, 'id, name, starts_on, weeks_total'))
      result.cohort = cohort
      const minutes = made.default_session_minutes || 90
      // the same shape as content/model.js sessionsForCohort: 1 a week, carrying its week, dated or not
      const list = weeks.length
        ? weeks.slice().sort((a, b) => a.sort_order - b.sort_order).map((m, i) => ({
          product_id: made.id, cohort_id: cohort.id, module_id: m.id, number: m.number, title: m.label,
          starts_at: plan[i]?.starts_at || null, duration_minutes: minutes, status: 'scheduled', updated_by: actorId,
        }))
        : plan.map(p => ({ product_id: made.id, cohort_id: cohort.id, number: p.number, title: p.title, starts_at: p.starts_at, duration_minutes: minutes, status: 'scheduled', updated_by: actorId }))
      if (list.length) result.sessions = (await insert('sessions', list)).length
    } catch { result.problems.push('cohort') }
  }

  if (made.type === 'on_demand') {
    try {
      result.modules = (await insert('modules', modulesFor(form.count).map(m => ({ ...m, product_id: made.id })), MODULE_COLUMNS)).length
    } catch { result.problems.push('weeks') }
  }

  if (made.type === 'mastermind') {
    try {
      const g = nextCohort(made, [])
      result.group = one(await insert('cohorts', {
        product_id: made.id, name: g.name, kind: 'group', number: g.number, sort_order: 1, created_by: actorId,
      }, 'id, name'))
    } catch { result.problems.push('group') }
  }

  const tabs = startingSections(made.type, pieces, made.access_kind)
  if (tabs.length) {
    try {
      result.tabs = (await insert('resource_sections', tabs.map((name, i) => ({ product_id: made.id, name, sort_order: i + 1 })))).length
    } catch { result.problems.push('tabs') }
  }

  const sells = paid && runsFor(made.type).length > 0
  if (owner && sells && form.prices?.length) {
    try {
      for (const price of form.prices) {
        await api.run(api.from('price_map').insert({
          stripe_price_id: price.id, stripe_product_id: price.product_id, label: labelFromStripeName(price.product_name) || made.name, status: 'mapped', updated_by: actorId,
        }).select('stripe_price_id'))
        await api.run(api.from('price_map_products').insert({ stripe_price_id: price.id, product_id: made.id }).select('stripe_price_id'))
      }
    } catch { result.problems.push('price') }
  }

  if (owner && sells && paidMonthly(row, form.prices)) result.held = true
  else if (owner && sells) {
    try {
      const billing = await setPurchasesLive(api, made.id)
      result.live = billing?.mode === 'live'
    } catch { result.problems.push('billing') }
  }

  if (form.cover) {
    try { await uploadCover(api, made.id, form.cover) } catch { result.problems.push('cover') }
  }
  return result
}

// A cover photo: sign-upload gives a 1-off upload link into the covers bucket, the file goes straight
// there, then sign-upload sets the product's cover_path (owner and admin)
export async function uploadCover(api, productId, file) {
  const contentType = file.type || 'image/jpeg'
  const start = await api.sign_upload({ action: 'start', kind: 'cover', product_id: productId, file_name: file.name, content_type: contentType, size_bytes: file.size })
  const { error } = await api.db.storage.from(start.bucket || 'covers').uploadToSignedUrl(start.path, start.token, file, { contentType })
  if (error) throw new AdminError('not_uploaded', 0, "The photo didn't upload. Try again.")
  return api.sign_upload({ action: 'finish', receipt: start.receipt })
}

// ---- Product settings ---------------------------------------------------------------------------

// 1 product by its slug, with what its settings page needs
export async function loadProductSettings(api, slug) {
  const rows = await api.run(api.from('catalogue').select(CATALOGUE_COLUMNS).eq('slug', slug))
  const p = rows[0] || null
  if (!p) return { product: null }
  const [billing, extras, offers, recordings, all] = await Promise.all([
    api.run(api.from('catalogue_billing').select(BILLING_COLUMNS).eq('product_id', p.id)),
    api.run(api.from('product_extras').select('product_id, whatsapp_url').eq('product_id', p.id)),
    p.type === 'masterclass' ? api.run(api.from('masterclass_offers').select('product_id, offer, owner_card').eq('product_id', p.id)) : [],
    p.type === 'masterclass' ? api.run(api.from('recordings').select('id, product_id, title, is_published').eq('product_id', p.id).order('sort_order')) : [],
    api.run(api.from('catalogue').select('id, slug, name, type, access_kind, state, moved_in').order('access_kind', { ascending: false }).order('sort_order')),
  ])
  const lengths = recordings.length
    ? await api.run(api.from('recording_content').select('recording_id, duration_seconds').in('recording_id', recordings.map(r => r.id)))
    : []
  return {
    product: p, billing: billing[0] || null, extras: extras[0] || null, offer: offers[0] || null,
    recording: recordings[0] || null, length: lengths.find(l => l.recording_id === recordings[0]?.id)?.duration_seconds || null,
    others: all,
  }
}

export function updateProduct(api, productId, patch) {
  return changed(api, api.from('catalogue').update(patch).eq('id', productId).select(CATALOGUE_COLUMNS))
}

// The Funding Engine's WhatsApp link (C12): 1 row per product, written or cleared
export async function saveWhatsapp(api, productId, url) {
  return one(await api.run(api.from('product_extras').upsert({ product_id: productId, whatsapp_url: url || null }, { onConflict: 'product_id' }).select('product_id, whatsapp_url')))
}

// The masterclass offer and the owner's card (owner only: guard_masterclass_offer refuses anyone else
// with 42501, and checks the shape and the links for everyone)
export async function saveOffer(api, productId, offer, ownerCard) {
  return one(await api.run(api.from('masterclass_offers').upsert({ product_id: productId, offer, owner_card: ownerCard }, { onConflict: 'product_id' }).select('product_id, offer, owner_card')))
}

// ---- Page layout --------------------------------------------------------------------------------

// 1 product by slug, with its layouts and each published version (owner and admin read layouts)
export async function loadLayouts(api, slug) {
  const rows = await api.run(api.from('catalogue').select('id, slug, path, name, type, access_kind, state, look, moved_in').eq('slug', slug))
  const p = rows[0] || null
  if (!p) return { product: null }
  const layouts = await api.run(api.from('layouts').select('id, product_id, page, draft_sections, has_draft, published_version_id, draft_updated_at').eq('product_id', p.id))
  const ids = layouts.map(l => l.published_version_id).filter(Boolean)
  const versions = ids.length ? await api.run(api.from('layout_versions').select('id, layout_id, version, sections, published_at').in('id', ids)) : []
  return { product: p, layouts, versions }
}

// The latest version number of a layout (the database checks the next is this + 1)
async function latestVersion(api, layoutId) {
  const rows = await api.run(api.from('layout_versions').select('version').eq('layout_id', layoutId).order('version', { ascending: false }).limit(1))
  return rows[0]?.version || 0
}

async function ensureLayout(api, product, page, layout, sections, actorId, draft) {
  if (layout) return layout
  return one(await api.run(api.from('layouts').insert({
    product_id: product.id, page, draft_sections: sections, has_draft: draft,
    draft_updated_by: draft ? actorId : null, draft_updated_at: draft ? new Date().toISOString() : null,
  }).select('id, product_id, page, draft_sections, has_draft, published_version_id, draft_updated_at')))
}

// Save draft: members still see the published layout
export async function saveDraft(api, product, page, layout, list, actorId) {
  const sections = sectionsToSave(product, page, list)
  const row = await ensureLayout(api, product, page, layout, sections, actorId, true)
  if (layout) {
    return changed(api, api.from('layouts').update({ draft_sections: sections, has_draft: true, draft_updated_by: actorId, draft_updated_at: new Date().toISOString() })
      .eq('id', row.id).select('id, product_id, page, draft_sections, has_draft, published_version_id, draft_updated_at'))
  }
  return row
}

// Publish: a new version (check_layout_sections refuses none shown or a fixed section hidden, and
// audits it as layout_published), then the layout points at it. 2 writes: if the second fails, the
// version is kept and publishing again points at the next 1.
export async function publishLayout(api, product, page, layout, list, actorId) {
  const sections = sectionsToSave(product, page, list)
  const row = await ensureLayout(api, product, page, layout, sections, actorId, false)
  const version = (await latestVersion(api, row.id)) + 1
  const v = one(await api.run(api.from('layout_versions').insert({ layout_id: row.id, version, sections, published_by: actorId }).select('id, layout_id, version, sections, published_at')))
  const updated = await changed(api, api.from('layouts').update({ published_version_id: v.id, draft_sections: sections, has_draft: false })
    .eq('id', row.id).select('id, product_id, page, draft_sections, has_draft, published_version_id, draft_updated_at'))
  return { layout: updated, version: v }
}
